import { Box } from "@mui/material";
import { Chart as ChartJS, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

import { ISummary } from "./types";

ChartJS.register(...registerables, ChartDataLabels);

interface IProps {
  beneficiaries: ISummary[];
}

interface ICityData {
  city: string;
  count: number;
}

export function GraphCity({ beneficiaries }: IProps) {
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  // Processa os dados por cidade
  const cityData: ICityData[] = useMemo(() => {
    const cityMap = new Map<string, number>();

    beneficiaries.forEach((beneficiary) => {
      let cityLabel = "Não identificado";
      
      if (beneficiary.city) {
        cityLabel = beneficiary.city;
      }

      const currentCount = cityMap.get(cityLabel) || 0;
      // Não mostra quantidade de pessoas sem cidade
      if (cityLabel !== "Não identificado") cityMap.set(cityLabel, currentCount + 1);
    });

    // Converte para array e ordena por quantidade (decrescente)
    const cityArray = Array.from(cityMap.entries()).map(([city, count]) => ({
      city,
      count,
    }));

    // Retorna top 10 cidades
    return cityArray.sort((a, b) => b.count - a.count).slice(0, 10);
  }, [beneficiaries]);

  useEffect(() => {
    const labels = cityData.map((item) => item.city);
    const data = cityData.map((item) => item.count);

    setChartData({
      labels: labels,
      datasets: [
        {
          label: "Número de vidas",
          data: data,
          backgroundColor: "#3300FF",
          borderColor: "#3300FF",
          borderWidth: 1,
          borderRadius: { bottomRight: 5, topRight: 5 },
          barThickness: 20,
        },
      ],
    });
  }, [cityData]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "400px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "center",
          paddingTop: "0.25rem",
          paddingBottom: "1rem",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: "Sora",
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: "2rem",
            color: "rgba(15, 15, 30, 0.6)",
          }}
        >
          Distribuição Geográfica
        </span>
      </Box>
      <Box
        sx={{
          padding: "1rem",
          flexGrow: 1,
          minHeight: 0,
          position: "relative",
        }}
      >
        <Bar
          data={chartData}
          options={{
            indexAxis: "y" as const,
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                right: 50,
              },
            },
            plugins: {
              datalabels: {
                align: "end",
                anchor: "end",
                color: "#000000",
                font: {
                  weight: "bold",
                  size: 14,
                },
                formatter: (value: number) => value.toLocaleString(),
                clip: false,
              },
              legend: {
                display: true,
                position: "top",
                align: "end",
                labels: {
                  usePointStyle: true,
                  color: "#1E1B39",
                  font: {
                    weight: "bold",
                    size: 14,
                    lineHeight: 18,
                  },
                },
              },
            },
            elements: {
              bar: {
                borderWidth: 2,
              },
            },
            scales: {
              x: {
                grid: {
                  display: true,
                  drawBorder: false,
                  borderDash: [10, 10],
                  color: "rgba(0, 0, 0, 0.1)",
                },
                ticks: {
                  color: "#1E1B39",
                },
              },
              y: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: "#1E1B39",
                  font: {
                    size: 14,
                  },
                },
              },
            },
            animation: {
              duration: 0,
            },
            onHover: undefined,
            onResize: undefined,
          }}
        />
      </Box>
    </Box>
  );
}
